import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Wallet, Sparkles } from "lucide-react";

function formatErr(d) {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(", ");
  return d?.msg || String(d);
}

export default function LoginPage() {
  const { login, register } = useAuth();
  const nav = useNavigate();

  const [lEmail, setLEmail] = useState("admin@billsystem.app");
  const [lPass, setLPass] = useState("Admin@123");
  const [rName, setRName] = useState("");
  const [rEmail, setREmail] = useState("");
  const [rPass, setRPass] = useState("");
  const [busy, setBusy] = useState(false);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(lEmail, lPass); toast.success("Welcome back!"); nav("/calendar"); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Login failed"); }
    finally { setBusy(false); }
  };
  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await register(rEmail, rPass, rName || rEmail.split("@")[0]); toast.success("Account created!"); nav("/calendar"); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Registration failed"); }
    finally { setBusy(false); }
  };

  const doGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/calendar";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center mb-3 shadow-lg">
            <Wallet size={26}/>
          </div>
          <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tight">The Daily Ledger</h1>
          <p className="text-xs uppercase tracking-[0.35em] text-muted-foreground mt-2 font-newspaper">Vol. I · No. 1 · Est. 2026 · A Monthly Bill Almanac</p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <Tabs defaultValue="login">
            <TabsList className="grid grid-cols-2 mb-5">
              <TabsTrigger value="login" data-testid="tab-login">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={doLogin} className="space-y-4">
                <div>
                  <Label htmlFor="l-email">Email</Label>
                  <Input id="l-email" type="email" value={lEmail} onChange={(e)=>setLEmail(e.target.value)}
                         required data-testid="login-email-input"/>
                </div>
                <div>
                  <Label htmlFor="l-pass">Password</Label>
                  <Input id="l-pass" type="password" value={lPass} onChange={(e)=>setLPass(e.target.value)}
                         required data-testid="login-password-input"/>
                </div>
                <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-btn">
                  {busy ? "Signing in…" : "Sign in"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={doRegister} className="space-y-4">
                <div>
                  <Label htmlFor="r-name">Name</Label>
                  <Input id="r-name" value={rName} onChange={(e)=>setRName(e.target.value)}
                         data-testid="register-name-input"/>
                </div>
                <div>
                  <Label htmlFor="r-email">Email</Label>
                  <Input id="r-email" type="email" value={rEmail} onChange={(e)=>setREmail(e.target.value)}
                         required data-testid="register-email-input"/>
                </div>
                <div>
                  <Label htmlFor="r-pass">Password (min 6 chars)</Label>
                  <Input id="r-pass" type="password" value={rPass} onChange={(e)=>setRPass(e.target.value)}
                         required minLength={6} data-testid="register-password-input"/>
                </div>
                <Button type="submit" className="w-full" disabled={busy} data-testid="register-submit-btn">
                  {busy ? "Creating…" : "Create account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-border"/>
            <span className="text-xs text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border"/>
          </div>

          <Button variant="outline" className="w-full" onClick={doGoogle} data-testid="google-signin-btn">
            <svg viewBox="0 0 48 48" width="18" height="18" className="mr-2">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.2 29.4 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.9 29 5 24 5 13.5 5 5 13.5 5 24s8.5 19 19 19 19-8.5 19-19c0-1.2-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.9 19 13 24 13c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.9 29 5 24 5 16.3 5 9.7 9.2 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 43c5 0 9.5-1.9 12.9-5l-6-4.9C29 34.5 26.6 35 24 35c-5.4 0-9.9-2.7-11.3-6.8l-6.5 5C9.6 38.7 16.2 43 24 43z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.8-3.7 5l6 4.9C41 34 43 29.5 43 24c0-1.2-.1-2.4-.4-3.5z"/>
            </svg>
            Continue with Google
          </Button>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Demo: <span className="font-mono">admin@billsystem.app</span> / <span className="font-mono">Admin@123</span>
        </p>
      </div>
    </div>
  );
}
